import { makeMonitorRepository } from "../repositories/repositoryBuilders.js";
import KeyFigureContextRepositoryWrapper from "../repositories/KeyFigureContextRepositoryWrapper.js";
import KeyFigurePreset from "../../models/keyFigure/preset/KeyFigurePreset.js";
import polyglot from "../polyglot/polyglot.js";
import session from "../session.js";
import visibleKeyFigures from "../../models/keyFigure/preset/visibleKeyFigures.js";

/**
 * Service used to manipulate `keyFigurePreset` instances.
 * It handles the communication with the back-end via a repository.
 *
 * @param {string} [companyId] - The identifier for the company owning
 * the key figure presets. If not provided, `route` and
 * `defaultFilters` are required.
 * @param {string} [route] - The repository route to send/receive key
 * figure presets to/from the back-end. If not provided, `companyId`
 * is required.
 * @param {object} [defaultFilters] - An object containing route
 * segments and their default values for the route. This is useful if
 * `route` has some segments that can be filled right now or if no
 * `route` is provided.
 * @param {KeyFigureService} [keyFigureService] - The service
 * responsible for fetching key figures. If not provided, use the
 * default from `KeyFigureContextRepositoryWrapper`.
 */
export default class KeyFigurePresetService {
	constructor({
		companyId,
		route = "/Shared/Company/#companyId/KeyFigurePreset/?id",
		defaultFilters = { companyId },
		keyFigureService,
	} = {}) {
		this._repository = new KeyFigureContextRepositoryWrapper({
			keyFigureService,
			repository: makeMonitorRepository({
				route,
				defaultFilters,
				model: {
					materialize: this._materializeKeyFigurePreset.bind(this),
				},
				antiForgeryToken: session.getAntiForgeryToken(),
			}),
		});
	}

	get(id) {
		return this._repository.get({ filter: { id } });
	}

	getVisibleKeyFigures() {
		return this.get("VisibleKeyFigures");
	}

	findAll(repositoryOptions) {
		return this._repository.get(repositoryOptions);
	}

	findAllVisiblePresets() {
		return this.getVisibleKeyFigures().then((visibleKeyFigurePreset) =>
			this.get().then((allPresets) =>
				allPresets.filter((preset) =>
					visibleKeyFigurePreset.isPresetUserVisible(preset),
				),
			),
		);
	}

	update(preset, repositoryOptions = {}) {
		return this._repository.update({
			model: preset,
			...repositoryOptions,
			filter: { id: preset.getId(), ...repositoryOptions.filter },
		});
	}

	save(preset, repositoryOptions) {
		return this._repository.save({ model: preset, ...repositoryOptions });
	}

	remove(id) {
		return this._repository.remove({ filter: { id } });
	}

	_materializeKeyFigurePreset(data, context) {
		if (data.isSystemPreset) {
			data.name = polyglot.translateServerString(data.name);
			data.description = polyglot.translateServerString(data.description);
		}

		return data.shortName === "VisibleKeyFigures"
			? this._materializeVisibleKeyFiguresPreset(data, context)
			: KeyFigurePreset.materialize(data, context);
	}

	_materializeVisibleKeyFiguresPreset(data, context) {
		const keyFigures = data.keyFigureIds
			.map((keyFigureId) => context.keyFigures[keyFigureId])
			.filter((keyFigure) => !!keyFigure);

		visibleKeyFigures.setKeyFigures(keyFigures);

		return visibleKeyFigures;
	}
}
